import React, { useEffect, useRef, useState } from "react";
import {
  Stack,
  TextField,
  FormControlLabel,
  Switch,
  Checkbox,
  Typography,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import addVisitorModel, { visitorTypeOptions } from "./addVisitorModel";
import { Field, useFormikContext } from "formik";
import { styles } from "../../theme/theme";
import DateTimeField from "../ui/formikControls/DateTimeField";
import DateField from "../ui/formikControls/DateField";
import { IN_DRIVE_VISITOR_TYPE } from "./addVisitorSchema";
import { useGetPropertiesOwnerByOwnerIdQuery } from "../../redux/residentPortalApi";
import { useAppSelector } from "../../redux/hooks";
import {
  developmentInfo,
  formatPropertyNumber,
} from "../../utils/propertyUtils";

const {
  formField: {
    firstName,
    lastName,
    visitorEmail,
    visitorWhatsApp,
    visitorType,
    purpose,
    visitDateTime,
    isMultiDay,
    startDate,
    endDate,
    propertyId,
    consent,
  },
} = addVisitorModel;

const sectionCardSx = {
  border: "1px solid #eaeef2",
  borderRadius: 3,
  p: 3,
  backgroundColor: "#fff",
  boxShadow: "0 2px 10px rgba(15, 34, 58, 0.04)",
};

const sectionTitleSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

// Millennium Heights has a shared property record with no specific unit, used for general/common-area access
const isGenericMillenniumHeights = (property?: {
  development?: string;
  phase?: string;
}) =>
  Boolean(property) &&
  property!.development === "millennium" &&
  !property!.phase?.startsWith("block-");

const destinationOptions = [
  { value: "site_visit", label: "Site Visit" },
  { value: "tour_with_client", label: "Tour with Client" },
  { value: "visiting_premises", label: "Visiting the Premises" },
  { value: "other", label: "Other" },
];

const AddVisitorForm = () => {
  const { errors, touched, values, setFieldValue } = useFormikContext<any>();
  const user = useAppSelector((state) => state.auth.user);

  // Current date for minimum date validation
  const currentDate = new Date();

  // Fetch user's properties
  const {
    data: userProperties,
    isLoading,
    error,
  } = useGetPropertiesOwnerByOwnerIdQuery(
    { ownerId: user?.id ?? "" },
    { skip: !user },
  );

  const selectedProperty = userProperties?.find(
    (property) => property.id === values[propertyId.name],
  );
  const isPokugaraProperty =
    selectedProperty?.development?.trim().toLowerCase() === "pokugara";

  useEffect(() => {
    if (isPokugaraProperty && values[visitorType.name] === "bnb") {
      setFieldValue(visitorType.name, "visitor");
    }
  }, [isPokugaraProperty, values[visitorType.name], setFieldValue]);

  const availableVisitorTypeOptions = visitorTypeOptions.filter(
    (option) => !isPokugaraProperty || option.value !== "bnb",
  );

  // Destination popup for the generic Millennium Heights property (no specific unit)
  const [showDestinationDialog, setShowDestinationDialog] = useState(false);
  const [destinationSelection, setDestinationSelection] = useState<
    string | null
  >(null);
  const [customDestination, setCustomDestination] = useState("");
  const destinationAnsweredForPropertyRef = useRef<string | null>(null);

  useEffect(() => {
    const selectedPropertyId = values[propertyId.name];
    if (!selectedPropertyId || !userProperties) return;

    const selectedProperty = userProperties.find(
      (property) => property.id === selectedPropertyId,
    );
    if (
      isGenericMillenniumHeights(selectedProperty) &&
      destinationAnsweredForPropertyRef.current !== selectedPropertyId
    ) {
      setDestinationSelection(null);
      setCustomDestination("");
      setShowDestinationDialog(true);
    }
  }, [values[propertyId.name], userProperties]);

  const closeDestinationDialog = (destination: string) => {
    setFieldValue(purpose.name, destination);
    destinationAnsweredForPropertyRef.current = values[propertyId.name];
    setShowDestinationDialog(false);
  };

  const handleDestinationOptionClick = (value: string) => {
    if (value === "other") {
      setDestinationSelection("other");
      return;
    }
    const label =
      destinationOptions.find((option) => option.value === value)?.label ??
      value;
    closeDestinationDialog(label);
  };

  const cancelDestinationDialog = () => {
    // Destination is mandatory for this shared property - clear the selection if the visitor cancels
    setFieldValue(propertyId.name, "");
    setShowDestinationDialog(false);
  };

  const handleMultiDayToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(isMultiDay.name, event.target.checked);
  };

  const isPurposeRequired = values[visitorType.name] === "other";
  const isInDrive = values[visitorType.name] === IN_DRIVE_VISITOR_TYPE;

  return (
    <Stack width="100%" spacing={4}>
      <Box sx={sectionCardSx}>
        <Typography
          variant="h6"
          color="#345678"
          gutterBottom
          sx={sectionTitleSx}
        >
          <HomeWorkOutlinedIcon fontSize="small" />
          Property Information
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select one of your properties to issue the invite from
        </Typography>

        <FormControl
          fullWidth
          variant={styles.textFieldVariant}
          error={touched[propertyId.name] && Boolean(errors[propertyId.name])}
          sx={{ mt: 1 }}
        >
          <InputLabel id="property-select-label">{propertyId.label}</InputLabel>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error loading your properties. Using default properties list.
            </Alert>
          ) : (
            <Field
              as={Select}
              labelId="property-select-label"
              id={propertyId.name}
              name={propertyId.name}
              label={propertyId.label}
              sx={{ borderRadius: 2 }}
            >
              {userProperties && userProperties.length > 0 ? (
                userProperties.map((property) => (
                  <MenuItem key={property.id} value={property.id}>
                    {
                      developmentInfo(
                        property.development,
                        property.phase,
                        property.type,
                      ).name
                    }{" "}
                    - {formatPropertyNumber(property)}
                  </MenuItem>
                ))
              ) : (
                <>
                  <MenuItem disabled value="">
                    <em>No properties found for your account</em>
                  </MenuItem>
                </>
              )}
            </Field>
          )}
          {touched[propertyId.name] && errors[propertyId.name] && (
            <Typography variant="caption" color="error">
              {/*{errors[propertyId.name]}*/}
            </Typography>
          )}
        </FormControl>
      </Box>

      <Box sx={sectionCardSx}>
        <Typography
          variant="h6"
          color="#345678"
          gutterBottom
          sx={sectionTitleSx}
        >
          <PersonOutlineIcon fontSize="small" />
          Visitor Information
        </Typography>

        <Stack spacing={3} mt={1}>
          <FormControl
            fullWidth
            variant={styles.textFieldVariant}
            error={
              touched[visitorType.name] && Boolean(errors[visitorType.name])
            }
          >
            <InputLabel id="visitor-type-label">{visitorType.label}</InputLabel>
            <Field
              as={Select}
              labelId="visitor-type-label"
              id={visitorType.name}
              name={visitorType.name}
              label={visitorType.label}
              sx={{ borderRadius: 2 }}
            >
              {availableVisitorTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Field>
          </FormControl>

          {!isInDrive && (
          <Stack direction={"row"} spacing={4}>
            <Field
              as={TextField}
              variant={styles.textFieldVariant}
              fullWidth
              {...firstName}
              type="text"
              autoComplete="off"
              error={touched[firstName.name] && Boolean(errors[firstName.name])}
              helperText={
                touched[firstName.name] && errors[firstName.name]
                  ? errors[firstName.name]
                  : ""
              }
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
            <Field
              as={TextField}
              variant={styles.textFieldVariant}
              fullWidth
              {...lastName}
              type="text"
              autoComplete="off"
              error={touched[lastName.name] && Boolean(errors[lastName.name])}
              helperText={
                touched[lastName.name] && errors[lastName.name]
                  ? errors[lastName.name]
                  : ""
              }
              InputProps={{
                sx: { borderRadius: 2 },
              }}
            />
          </Stack>
          )}

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              Provide at least one way to reach the visitor
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={4}>
              <Field
                as={TextField}
                variant={styles.textFieldVariant}
                fullWidth
                {...visitorEmail}
                type="email"
                autoComplete="off"
                error={
                  touched[visitorEmail.name] &&
                  Boolean(errors[visitorEmail.name])
                }
                helperText={
                  touched[visitorEmail.name] && errors[visitorEmail.name]
                    ? errors[visitorEmail.name]
                    : ""
                }
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
              <Field
                as={TextField}
                variant={styles.textFieldVariant}
                fullWidth
                {...visitorWhatsApp}
                autoComplete="off"
                error={
                  touched[visitorWhatsApp.name] &&
                  Boolean(errors[visitorWhatsApp.name])
                }
                helperText={
                  touched[visitorWhatsApp.name] && errors[visitorWhatsApp.name]
                    ? errors[visitorWhatsApp.name]
                    : ""
                }
                InputProps={{
                  sx: { borderRadius: 2 },
                }}
              />
            </Stack>
          </Box>

          <Field
            as={TextField}
            variant={styles.textFieldVariant}
            fullWidth
            {...purpose}
            required={isPurposeRequired}
            type="text"
            autoComplete="off"
            placeholder="e.g. House cleaning, food delivery, family visit"
            helperText={
              touched[purpose.name] && errors[purpose.name]
                ? errors[purpose.name]
                : isPurposeRequired
                ? "Required when Visitor Type is Other"
                : "Optional"
            }
            error={touched[purpose.name] && Boolean(errors[purpose.name])}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />
        </Stack>
      </Box>

      <Box sx={sectionCardSx}>
        <Typography
          variant="h6"
          color="#345678"
          gutterBottom
          sx={sectionTitleSx}
        >
          <EventAvailableOutlinedIcon fontSize="small" />
          Visit Details
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={values[isMultiDay.name]}
              onChange={handleMultiDayToggle}
              name={isMultiDay.name}
              color="primary"
            />
          }
          label={isMultiDay.label}
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 1,
            backgroundColor: values[isMultiDay.name]
              ? "#f0f7ff"
              : "transparent",
          }}
        />

        {!values[isMultiDay.name] ? (
          // Single day invite - show date time picker
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 2,
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="body2" color="#666" gutterBottom>
              Select date and time for the visit
            </Typography>
            <Field
              component={DateTimeField}
              variant={styles.textFieldVariant}
              {...visitDateTime}
              fullWidth
              type="text"
              autoComplete="off"
              minDate={currentDate}
              error={
                touched[visitDateTime.name] &&
                Boolean(errors[visitDateTime.name])
              }
              helperText={
                touched[visitDateTime.name] && errors[visitDateTime.name]
                  ? errors[visitDateTime.name]
                  : ""
              }
              sx={{ borderRadius: 2 }}
            />
          </Box>
        ) : (
          // Multi-day invite - show date range pickers
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 2,
              backgroundColor: "#f0f7ff",
            }}
          >
            <Typography variant="body2" color="#666" gutterBottom>
              Select date range for the visit
            </Typography>
            <Stack direction={"row"} spacing={4}>
              <Field
                component={DateField}
                variant={styles.textFieldVariant}
                {...startDate}
                fullWidth
                type="text"
                autoComplete="off"
                minDate={currentDate}
                error={
                  touched[startDate.name] && Boolean(errors[startDate.name])
                }
                helperText={
                  touched[startDate.name] && errors[startDate.name]
                    ? errors[startDate.name]
                    : ""
                }
                sx={{ borderRadius: 2 }}
                label="Start Date"
              />
              <Field
                component={DateField}
                variant={styles.textFieldVariant}
                {...endDate}
                fullWidth
                type="text"
                autoComplete="off"
                minDate={currentDate}
                error={touched[endDate.name] && Boolean(errors[endDate.name])}
                helperText={
                  touched[endDate.name] && errors[endDate.name]
                    ? errors[endDate.name]
                    : ""
                }
                sx={{ borderRadius: 2 }}
                label="End Date"
              />
            </Stack>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          border: "1px solid #eaeef2",
          borderRadius: 3,
          p: 3,
          backgroundColor: values[consent.name] ? "#f4f9f6" : "#fffaf0",
          transition: "background-color 0.2s ease",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(values[consent.name])}
              onChange={(event) =>
                setFieldValue(consent.name, event.target.checked)
              }
              onBlur={() =>
                setFieldValue(consent.name, values[consent.name], true)
              }
              name={consent.name}
              color="primary"
            />
          }
          label={
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <VerifiedUserOutlinedIcon
                fontSize="small"
                color="action"
                sx={{ mt: "2px" }}
              />
              <Typography variant="body2">{consent.label}</Typography>
            </Stack>
          }
        />
        {touched[consent.name] && errors[consent.name] && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", ml: 5 }}
          >
            {String(errors[consent.name])}
          </Typography>
        )}
      </Box>

      <Dialog
        open={showDestinationDialog}
        onClose={cancelDestinationDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Where is the visitor going?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Millennium Heights is a shared entrance with no specific unit
            selected. Please specify the visitor's destination.
          </Typography>
          <Stack spacing={1.5}>
            {destinationOptions.map((option) => (
              <Button
                key={option.value}
                variant={
                  destinationSelection === option.value
                    ? "contained"
                    : "outlined"
                }
                onClick={() => handleDestinationOptionClick(option.value)}
                sx={{ justifyContent: "flex-start", borderRadius: 2 }}
              >
                {option.label}
              </Button>
            ))}
            {destinationSelection === "other" && (
              <TextField
                autoFocus
                fullWidth
                label="Specify destination"
                value={customDestination}
                onChange={(e) => setCustomDestination(e.target.value)}
                placeholder="e.g. Sales office, Block 2 rooftop"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDestinationDialog} color="secondary">
            Cancel
          </Button>
          {destinationSelection === "other" && (
            <Button
              variant="contained"
              disabled={!customDestination.trim()}
              onClick={() => closeDestinationDialog(customDestination.trim())}
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AddVisitorForm;
